import { ActionIcon, Button, Checkbox, Group, MultiSelect, NumberInput, Text, TextInput, Tooltip } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from '../DatePicker';

const RuleFilter = ({ filter, updateFilter, removeFilter, filterOptions, readOnly, ruleInverseSelection }) => {
  const [forceVertical, setForceVertical] = useState(false);
  const dateRangeRef = useRef(null);
  const handleArrayValuesChange = (value) => {
    updateFilter({ ...filter, values: (value || '').split('|') });
  };

  const handleTypeaheadValueChange = (values) => {
    updateFilter({ ...filter, values });
  };

  const handleNumberElementChange = (value, index) => {
    const values = [...(filter.values || [])];
    values[index] = value;
    updateFilter({ ...filter, values });
  };

  const handleCheckbox = (checked, index) => {
    const values = [...(filter.values || [])];
    values[index] = checked;
    updateFilter({ ...filter, values });
  };

  const setDate = (index, date) => {
    const updatedFilter = { ...filter };
    updatedFilter.values = [...(filter.values || [])];
    updatedFilter.values[index] = date;
    updateFilter(updatedFilter);
  };

  const setDateDebounced = useMemo(
    () =>
      AwesomeDebouncePromise((index, date) => {
        const updatedFilter = { ...filter };
        updatedFilter.values = [...(filter.values || [])];
        updatedFilter.values[index] = date;
        updateFilter(updatedFilter);
      }, 300),
    [filter, updateFilter],
  );

  // Check if we need to force vertical layout for date ranges
  useEffect(() => {
    if (filter?.inputType === 'dateRange' && dateRangeRef.current) {
      const checkLayout = () => {
        const container = dateRangeRef.current;
        const containerWidth = container.offsetWidth;
        // Force vertical if container is less than 400px wide
        setForceVertical(containerWidth < 400);
      };

      checkLayout();
      window.addEventListener('resize', checkLayout);
      return () => window.removeEventListener('resize', checkLayout);
    }
  }, [filter?.inputType]);

  const RemoveFilterButton = () => (
    <Tooltip label="Remove this filter" position="top">
      <ActionIcon variant="outline" color="gray" size="sm" onClick={removeFilter} disabled={readOnly} ml="xs">
        <IconX size={14} />
      </ActionIcon>
    </Tooltip>
  );

  // DatePicker renders its children inline rather than inside a popover, so `disabled` on the
  // picker does not reach these - they have to be gated on readOnly themselves.
  const QuickDateButton = ({ onClick, children }) => (
    <Button size="xs" variant="subtle" onClick={onClick} disabled={readOnly} p={4} style={{ fontSize: '0.75rem' }}>
      {children}
    </Button>
  );

  const extractHourFromTemplate = (value) => {
    if (!value || typeof value !== 'string') return '';
    const match = value.match(/\{\{todayAtHour (\d+)\}\}/);
    return match ? parseInt(match[1], 10) : '';
  };

  const handleHourChange = (index, value) => {
    if (value === '' || value === null || value === undefined) return;
    const hour = parseInt(value, 10);
    if (isNaN(hour) || hour < 0 || hour > 23) return;

    const otherIndex = index === 0 ? 1 : 0;
    const otherHour = extractHourFromTemplate(filter.values?.[otherIndex]);

    if (otherHour !== '') {
      // If setting "From" (index 0), it must be less than "To"
      if (index === 0 && hour >= otherHour) return;
      // If setting "To" (index 1), it must be greater than "From"
      if (index === 1 && hour <= otherHour) return;
    }

    setDateDebounced(index, `{{todayAtHour ${hour}}}`);
  };

  if (!filter) {
    return null;
  }

  const renderInput = () => {
    switch (filter.inputType) {
      case 'string':
        return (
          <TextInput
            size="sm"
            label={filter.name}
            disabled={readOnly}
            value={(filter.values || []).join('|')}
            placeholder="Values separated by |"
            onChange={(e) => handleArrayValuesChange(e.target.value)}
          />
        );

      case 'select':
        return (
          <div>
            <Text size="sm" fw={600} c="dimmed" mb="xs">
              {filter.name}
            </Text>
            <MultiSelect
              disabled={readOnly}
              searchable
              placeholder="Select options..."
              value={filter.values.map((v) => v?.toString())}
              data={Array.from(
                new Map(
                  filterOptions.map((o) => [o.id.toString(), { label: o.label, value: o.id.toString() }])
                ).values()
              )}
              onChange={handleTypeaheadValueChange}
            />
          </div>
        );

      case 'range': {
        const isInvalidFrom =
          filter.values?.[0] !== undefined &&
          filter.values[0] !== '' &&
          ((filter.min !== undefined && parseFloat(filter.values[0]) < filter.min) ||
            (filter.max !== undefined && parseFloat(filter.values[0]) > filter.max) ||
            (filter.step !== undefined && parseFloat(filter.values[0]) % filter.step !== 0));

        const isInvalidTo =
          filter.values?.[1] !== undefined &&
          filter.values[1] !== '' &&
          ((filter.min !== undefined && parseFloat(filter.values[1]) < filter.min) ||
            (filter.max !== undefined && parseFloat(filter.values[1]) > filter.max) ||
            (filter.step !== undefined && parseFloat(filter.values[1]) % filter.step !== 0));

        return (
          <div>
            <Text size="sm" fw={600} c="dimmed" mb="xs">
              {filter.name}
            </Text>
            <div className="range-inputs">
              <style>{`
                .range-inputs {
                  display: flex;
                  flex-direction: column;
                  gap: 8px;
                }

                @media (min-width: 768px) {
                  .range-inputs {
                    flex-direction: row;
                    align-items: center;
                  }

                  .range-inputs > div {
                    flex: 1;
                  }

                  .range-separator {
                    margin: 0 8px;
                    flex-shrink: 0;
                  }
                }
              `}</style>
              <div>
                <NumberInput
                  size="sm"
                  disabled={readOnly}
                  error={isInvalidFrom}
                  step={filter.step}
                  min={filter.min}
                  max={filter.max}
                  value={filter.values?.[0] || ''}
                  placeholder={filter.placeholder?.[0] || 'From'}
                  onChange={(value) => handleNumberElementChange(value, 0)}
                />
              </div>
              <Text size="sm" c="dimmed" ta="center" className="range-separator">
                to
              </Text>
              <div>
                <NumberInput
                  size="sm"
                  disabled={readOnly}
                  error={isInvalidTo}
                  step={filter.step}
                  min={filter.min}
                  max={filter.max}
                  value={filter.values?.[1] || ''}
                  placeholder={filter.placeholder?.[1] || 'To'}
                  onChange={(value) => handleNumberElementChange(value, 1)}
                />
              </div>
            </div>
          </div>
        );
      }

      case 'dateRange': {
        const shouldStack = forceVertical || window.innerWidth < 768;

        return (
          <div>
            <Text size="sm" fw={600} c="dimmed" mb="xs">
              {filter.name}
            </Text>
            <div
              ref={dateRangeRef}
              className="date-range-container"
              style={{
                display: 'flex',
                flexDirection: shouldStack ? 'column' : 'row',
                gap: shouldStack ? '12px' : '8px',
                alignItems: shouldStack ? 'stretch' : 'flex-start',
              }}
            >
              <div
                style={{
                  width: shouldStack ? '100%' : '50%',
                  minWidth: shouldStack ? '200px' : '180px',
                }}
              >
                {/* this is in UTC */}
                <DatePicker
                  disabled={readOnly}
                  prepend="From"
                  size="sm"
                  value={filter.values?.[0]}
                  onChange={(date) => setDate(0, date)}
                >
                  <Group gap={4} wrap="wrap">
                    <QuickDateButton onClick={() => setDate(0, '{{todayStart}}')}>Today</QuickDateButton>
                    <Text size="xs" c="dimmed">
                      |
                    </Text>
                    <NumberInput
                      size="xs"
                      placeholder="Hour"
                      min={0}
                      max={23}
                      disabled={readOnly}
                      style={{ width: 70 }}
                      value={extractHourFromTemplate(filter.values?.[0])}
                      onChange={(value) => handleHourChange(0, value)}
                    />
                    <Text size="xs" c="dimmed">
                      |
                    </Text>
                    <QuickDateButton onClick={() => setDate(0, '{{daysFromNowStart 5}}')}>+5d</QuickDateButton>
                    <Text size="xs" c="dimmed">
                      |
                    </Text>
                    <QuickDateButton onClick={() => setDate(0, '{{hoursFromNowStart 5}}')}>+5h</QuickDateButton>
                    <Text size="xs" c="dimmed">
                      |
                    </Text>
                    <QuickDateButton onClick={() => setDate(0, '{{daysAgoStart 5}}')}>-5d</QuickDateButton>
                    <Text size="xs" c="dimmed">
                      |
                    </Text>
                    <QuickDateButton onClick={() => setDate(0, '{{hoursAgoStart 5}}')}>-5h</QuickDateButton>
                  </Group>
                </DatePicker>
              </div>
              <div
                style={{
                  width: shouldStack ? '100%' : '50%',
                  minWidth: shouldStack ? '200px' : '180px',
                }}
              >
                {/* this is in UTC */}
                <DatePicker
                  disabled={readOnly}
                  prepend="To"
                  size="sm"
                  value={filter.values?.[1]}
                  onChange={(date) => setDate(1, date)}
                >
                  <Group gap={4} wrap="wrap">
                    <QuickDateButton onClick={() => setDate(1, '{{todayEnd}}')}>Today</QuickDateButton>
                    <Text size="xs" c="dimmed">
                      |
                    </Text>
                    <NumberInput
                      size="xs"
                      placeholder="Hour"
                      min={0}
                      max={23}
                      disabled={readOnly}
                      style={{ width: 70 }}
                      value={extractHourFromTemplate(filter.values?.[1])}
                      onChange={(value) => handleHourChange(1, value)}
                    />
                    <Text size="xs" c="dimmed">
                      |
                    </Text>
                    <QuickDateButton onClick={() => setDate(1, '{{daysFromNowEnd 5}}')}>+5d</QuickDateButton>
                    <Text size="xs" c="dimmed">
                      |
                    </Text>
                    <QuickDateButton onClick={() => setDate(1, '{{hoursFromNowEnd 5}}')}>+5h</QuickDateButton>
                    <Text size="xs" c="dimmed">
                      |
                    </Text>
                    <QuickDateButton onClick={() => setDate(1, '{{daysAgoEnd 5}}')}>-5d</QuickDateButton>
                    <Text size="xs" c="dimmed">
                      |
                    </Text>
                    <QuickDateButton onClick={() => setDate(1, '{{hoursAgoEnd 5}}')}>-5h</QuickDateButton>
                  </Group>
                </DatePicker>
              </div>
            </div>
          </div>
        );
      }

      case 'checkbox': {
        const checkboxElement = (
          <Checkbox
            size="sm"
            disabled={readOnly}
            checked={filter.values?.[0] || false}
            onChange={(e) => handleCheckbox(e.currentTarget.checked, 0)}
            mt={4}
          />
        );

        return (
          <Group align="center" gap="md">
            <Text size="sm" fw={600} c="dimmed">
              {filter.name}
            </Text>
            {filter.conditionType === 'exists' ? (
              <Tooltip
                label={`Profiles with${ruleInverseSelection ? 'out' : ''} a ${filter.name.toLowerCase()}`}
                position="top"
              >
                <div>{checkboxElement}</div>
              </Tooltip>
            ) : (
              checkboxElement
            )}
          </Group>
        );
      }

      case 'number': {
        const isInvalid =
          filter.values?.[0] !== undefined &&
          filter.values[0] !== '' &&
          ((filter.min !== undefined && parseFloat(filter.values[0]) < filter.min) ||
            (filter.max !== undefined && parseFloat(filter.values[0]) > filter.max) ||
            (filter.step !== undefined && parseFloat(filter.values[0]) % filter.step !== 0));

        return (
          <NumberInput
            size="sm"
            label={filter.name}
            disabled={readOnly}
            error={isInvalid}
            step={filter.step}
            min={filter.min}
            max={filter.max}
            value={filter.values?.[0] || ''}
            placeholder={filter.placeholder?.[0]}
            onChange={(value) => handleNumberElementChange(value, 0)}
          />
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="filter-container">
      <style>{`
        .filter-container {
          padding: 1rem;
          border: 1px solid #dee2e6;
          border-radius: 0.375rem;
          background-color: #fff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          margin-bottom: 0.75rem;
          position: relative;
        }

        .filter-content {
          display: flex;
          align-items: flex-start;
          gap: 8px;
        }

        .filter-input {
          flex: 1;
          min-width: 0;
        }

        .filter-remove {
          flex-shrink: 0;
          align-self: flex-start;
        }

        @media (max-width: 576px) {
          .filter-container {
            padding: 0.75rem;
          }

          .filter-content {
            flex-direction: column;
          }

          .filter-remove {
            align-self: flex-end;
            position: absolute;
            top: 0.5rem;
            right: 0.5rem;
          }
        }
      `}</style>

      <div className="filter-content">
        <div className="filter-input">{renderInput()}</div>
        {!filter.required && (
          <div className="filter-remove">
            <RemoveFilterButton />
          </div>
        )}
      </div>
    </div>
  );
};

export default RuleFilter;
